/**
 * The text of the artist Profile banners (design `profile-card/b Profile Banners`, copies in
 * `docs/superpowers/design-reference/profile-card/`): the YouTube banner and the Facebook cover
 * carry the artist name, the line of the newest release and the SmartLink address. The Spotify
 * header never carries text (Spotify for Artists image rules: no text, no release promotion).
 *
 * This file is the one source of the text variants, the copy, the text boxes and the layout. The
 * server renders the file with it (`music24-api/src/artistProfile/banner/`), the web preview draws
 * it. No imports outside fym-shared. Pure.
 */
import type { ArtistProfileKind } from './artistProfile';
import { RELEASE_THEMES, themeType, type KitFontId, type ReleaseThemeId, type ThemeTypeStyle } from './releaseThemes';
import { textWidthEm } from './story/kitFontMetrics';
import { isReleasedOn, releaseDateLabel, STORY_COPY } from './story/storyCopy';

// ---------------------------------------------------------------------------------------------
// Variants and facts
// ---------------------------------------------------------------------------------------------

/** The text of a banner: none, the name, the name and the release line, or all with the link. */
export const PROFILE_BANNER_TEXTS = ['none', 'name', 'release', 'link'] as const;
export type ProfileBannerText = (typeof PROFILE_BANNER_TEXTS)[number];

/** The design default: "Name + release". */
export const PROFILE_BANNER_TEXT_DEFAULT: ProfileBannerText = 'release';

export function isProfileBannerText(value: unknown): value is ProfileBannerText {
  return typeof value === 'string' && (PROFILE_BANNER_TEXTS as readonly string[]).includes(value);
}

/** The kinds that take text. The Spotify header, the artist photo and the press photos never do. */
export const PROFILE_TEXT_KINDS = ['youtubeBanner', 'facebookCover'] as const;
export type ProfileTextKind = (typeof PROFILE_TEXT_KINDS)[number];

export function isProfileTextKind(kind: ArtistProfileKind | string): kind is ProfileTextKind {
  return (PROFILE_TEXT_KINDS as readonly string[]).includes(kind);
}

/**
 * The facts that a banner text was made from. The server stores them on the version
 * (`params.bannerFacts`), so a later date redo finds the banners that show an old title or date.
 */
export interface ProfileBannerFacts {
  /** The name of the artist (the Spotify for Artists name, else the account name). */
  artistName: string;
  /** The newest or next release, or null when the artist has no song. */
  release: {
    catalogItemUuid: string;
    title: string;
    /** `YYYY-MM-DD`, or null. */
    releaseDate: string | null;
    /** False for an unreleased catalog row: the banner asks for the pre-save. */
    released: boolean;
  } | null;
  /** The SmartLink address with no scheme (`laziibee.com`, `laziibee.music24.link/forests`), or null. */
  link: string | null;
}

/** Before the release day the banner asks for the pre-save; from the release day it says "Out now". */
export type ProfileBannerPhase = 'pre' | 'out';

/** The phase of a release on a day (`YYYY-MM-DD`). Pure. */
export function profileBannerPhase(
  release: NonNullable<ProfileBannerFacts['release']>,
  today: string
): ProfileBannerPhase {
  if (!release.released) {
    return 'pre';
  }
  return isReleasedOn(release.releaseDate, today) ? 'out' : 'pre';
}

export const PROFILE_BANNER_COPY = {
  newSingle: STORY_COPY.newSingle,
  outNow: (title: string): string => `${title} · Out now`,
  outOn: (title: string, date: string): string => `${title} · Out ${date}`,
  comingSoon: (title: string): string => `${title} · ${STORY_COPY.comingSoon}`,
  preSaveAt: (link: string): string => `Pre-save at ${link}`,
  listenAt: (link: string): string => `Listen at ${link}`,
} as const;

/** The texts of one banner. English, like the kit files. */
export interface ProfileBannerCopy {
  eyebrow: string | null;
  name: string;
  line: string | null;
  link: string | null;
  /** True: the release cover sits next to the text. */
  showCover: boolean;
  phase: ProfileBannerPhase | null;
}

/**
 * The texts of a variant (design b3): before the release day "New single", "<title> · Out
 * <date>", "Pre-save at <link>"; from the release day "<title> · Out now", "Listen at <link>". The
 * name alone has no date. A variant whose part is missing shows what it has. Null for `none` or an
 * empty name. Pure.
 */
export function profileBannerCopy(
  variant: ProfileBannerText,
  facts: ProfileBannerFacts,
  today: string
): ProfileBannerCopy | null {
  const name = facts.artistName.trim();
  if (variant === 'none' || name === '') {
    return null;
  }
  const { release } = facts;
  if (variant === 'name' || release === null) {
    const link = variant === 'link' && facts.link !== null ? PROFILE_BANNER_COPY.listenAt(facts.link) : null;
    return { eyebrow: null, name, line: null, link, showCover: false, phase: null };
  }
  const phase = profileBannerPhase(release, today);
  const date = releaseDateLabel(release.releaseDate);
  let line: string;
  if (phase === 'out') {
    line = PROFILE_BANNER_COPY.outNow(release.title);
  } else if (date !== null) {
    line = PROFILE_BANNER_COPY.outOn(release.title, date);
  } else {
    line = PROFILE_BANNER_COPY.comingSoon(release.title);
  }
  let link: string | null = null;
  if (variant === 'link' && facts.link !== null) {
    link = phase === 'out' ? PROFILE_BANNER_COPY.listenAt(facts.link) : PROFILE_BANNER_COPY.preSaveAt(facts.link);
  }
  return { eyebrow: phase === 'pre' ? PROFILE_BANNER_COPY.newSingle : null, name, line, link, showCover: true, phase };
}

// ---------------------------------------------------------------------------------------------
// Boxes and layout
// ---------------------------------------------------------------------------------------------

export interface ShareBox {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
}

/**
 * The box that holds every text (shares of the file):
 * - YouTube publishes its safe area: 1546 x 423 in the middle of 2560 x 1440. Phones, desktops
 *   and TVs show it in full.
 * - Facebook (help pages, read 2026-10-20): a computer shows the cover at 16:9 from the left
 *   (x 0 to 0.658), a phone at 2.4:1 in the middle (x 0.056 to 0.944), and the profile picture
 *   covers the bottom left on a phone. The box is the part that both crops show, clear of it.
 */
export const PROFILE_BANNER_TEXT_BOXES: Readonly<Record<ProfileTextKind, ShareBox>> = {
  youtubeBanner: { x0: 0.198, x1: 0.802, y0: 0.353, y1: 0.647 },
  facebookCover: { x0: 0.09, x1: 0.63, y0: 0.14, y1: 0.7 },
};

/**
 * The sizes in shares of the file width (design `bn.css`, 1cqw = 0.01). The name alone is larger.
 * `align`: YouTube centres the group in the safe area, Facebook starts at the left of its box.
 */
const SIZES: Readonly<
  Record<
    ProfileTextKind,
    {
      name: number;
      nameOnly: number;
      line: number;
      link: number;
      eyebrow: number;
      cover: number;
      gap: number;
      colGap: number;
      align: 'center' | 'left';
    }
  >
> = {
  youtubeBanner: {
    name: 0.044,
    nameOnly: 0.066,
    line: 0.0145,
    link: 0.0125,
    eyebrow: 0.0105,
    cover: 0.125,
    gap: 0.02,
    colGap: 0.007,
    align: 'center',
  },
  facebookCover: {
    name: 0.044,
    nameOnly: 0.064,
    line: 0.019,
    link: 0.016,
    eyebrow: 0.0135,
    cover: 0.15,
    gap: 0.02,
    colGap: 0.007,
    align: 'left',
  },
};

/** A text shrinks to fit its line down to this share of its size; below that it is cut with "…". */
export const PROFILE_BANNER_MIN_SCALE = 0.7;
/** The line height of the banner texts. */
export const PROFILE_BANNER_LINE_HEIGHT = 1.12;

export type ProfileBannerRole = 'eyebrow' | 'name' | 'line' | 'link';

/** One text of the layout, in file pixels. `y` is the top of its line box. */
export interface ProfileBannerTextItem {
  role: ProfileBannerRole;
  text: string;
  x: number;
  y: number;
  /** The widest the text may be: the server shrinks a text that measures wider. */
  maxWidth: number;
  sizePx: number;
  fontId: KitFontId;
  style: ThemeTypeStyle;
  /** `text`: the text colour of the theme; `accent`: the accent colour. */
  colour: 'text' | 'accent';
  /** 0..1, the alpha of the colour. */
  alpha: number;
  /** Neon: a soft glow of the text colour. */
  glow: boolean;
}

export interface ProfileBannerLayout {
  /** The release cover next to the text, or null. */
  cover: { x: number; y: number; size: number; radius: number } | null;
  texts: ProfileBannerTextItem[];
}

/** The text as the theme case shows it (Neon is upper case, Dreamy lower case). Pure. */
export function caseOf(text: string, style: Pick<ThemeTypeStyle, 'case'>): string {
  if (style.case === 'uppercase') {
    return text.toUpperCase();
  }
  if (style.case === 'lowercase') {
    return text.toLowerCase();
  }
  return text;
}

/**
 * A text that fits `widthPx`: the size shrinks until the text fits, down to
 * `PROFILE_BANNER_MIN_SCALE` of it; a text that is still too wide is cut with "…" (owner finding:
 * a long title ran past the Facebook desktop crop). The cut keeps the part after the last " · "
 * (the date of "<title> · Out 14 Nov"), so the title is cut and the date stays. Pure.
 */
export function fitBannerText(
  text: string,
  fontId: KitFontId,
  style: ThemeTypeStyle,
  sizePx: number,
  widthPx: number
): { text: string; sizePx: number } {
  const dot = text.lastIndexOf(' · ');
  const head = dot > 0 ? text.slice(0, dot) : text;
  const tail = dot > 0 ? text.slice(dot) : '';
  const widthAt = (value: string, size: number) => textWidthEm(caseOf(value, style), fontId, style) * size;
  const full = widthAt(text, sizePx);
  if (full <= widthPx || full <= 0) {
    return { text, sizePx };
  }
  const scaled = (sizePx * widthPx) / full;
  const floor = sizePx * PROFILE_BANNER_MIN_SCALE;
  if (scaled >= floor) {
    return { text, sizePx: Math.floor(scaled * 100) / 100 };
  }
  const chars = Array.from(head);
  for (let keep = chars.length - 1; keep > 0; keep -= 1) {
    const cut = `${chars.slice(0, keep).join('').trimEnd()}…${tail}`;
    if (widthAt(cut, floor) <= widthPx) {
      return { text: cut, sizePx: Math.floor(floor * 100) / 100 };
    }
  }
  return { text: `…${tail}`, sizePx: Math.floor(floor * 100) / 100 };
}

/**
 * The layout of the banner texts in file pixels (design b1, b2): the cover square, then a column of
 * the eyebrow, the name, the release line and the link, all inside the text box of the kind. The
 * display font of the look sets the name, the text font the lines. A long name or title shrinks or
 * is cut, and the column shrinks when it is taller than the box. Pure.
 */
export function profileBannerLayout(input: {
  kind: ProfileTextKind;
  size: { width: number; height: number };
  copy: ProfileBannerCopy;
  look: { themeId: ReleaseThemeId };
  /** False: the release has no cover, the column takes its place. */
  hasCover: boolean;
}): ProfileBannerLayout {
  const { kind, size, copy } = input;
  const sizes = SIZES[kind];
  const share = PROFILE_BANNER_TEXT_BOXES[kind];
  const box = {
    x: share.x0 * size.width,
    y: share.y0 * size.height,
    w: (share.x1 - share.x0) * size.width,
    h: (share.y1 - share.y0) * size.height,
  };
  const u = size.width;
  const type = themeType({ themeId: input.look.themeId, fontOverride: null });
  const glow = RELEASE_THEMES[input.look.themeId].cover.textEffect === 'glow';
  const withCover = copy.showCover && input.hasCover;
  const coverSize = withCover ? Math.min(sizes.cover * u, box.h) : 0;
  const gap = withCover ? sizes.gap * u : 0;
  const columnWidth = box.w - coverSize - gap;

  const rows: Array<{ role: ProfileBannerRole; text: string; base: number; display: boolean }> = [];
  if (copy.eyebrow !== null) {
    rows.push({ role: 'eyebrow', text: copy.eyebrow, base: sizes.eyebrow * u, display: false });
  }
  const nameOnly = copy.line === null && copy.link === null;
  rows.push({
    role: 'name',
    text: copy.name,
    base: (nameOnly ? sizes.nameOnly : sizes.name) * u * type.display.scale,
    display: true,
  });
  if (copy.line !== null) {
    rows.push({ role: 'line', text: copy.line, base: sizes.line * u, display: false });
  }
  if (copy.link !== null) {
    rows.push({ role: 'link', text: copy.link, base: sizes.link * u, display: false });
  }

  const fitted = rows.map((row) => {
    const fontId = row.display ? type.fonts.display : type.fonts.text;
    const style = row.display ? type.display : type.text;
    const fit = fitBannerText(row.text, fontId, style, row.base, columnWidth);
    return { ...row, ...fit, fontId, style };
  });
  const colGap = sizes.colGap * u;
  const heightOf = (scale: number) =>
    fitted.reduce((sum, row) => sum + row.sizePx * scale * PROFILE_BANNER_LINE_HEIGHT, 0) +
    colGap * scale * (fitted.length - 1);
  const scale = Math.min(1, box.h / Math.max(1, heightOf(1)));
  const columnHeight = heightOf(scale);
  const widest = Math.max(
    ...fitted.map((row) => textWidthEm(caseOf(row.text, row.style), row.fontId, row.style) * row.sizePx * scale)
  );
  const groupWidth = coverSize + gap + Math.min(columnWidth, widest);
  const left = sizes.align === 'center' ? box.x + (box.w - groupWidth) / 2 : box.x;
  const columnX = left + coverSize + gap;
  const centreText = sizes.align === 'center' && !withCover;

  let y = box.y + (box.h - columnHeight) / 2;
  const texts = fitted.map((row): ProfileBannerTextItem => {
    const sizePx = Math.round(row.sizePx * scale * 100) / 100;
    const width = textWidthEm(caseOf(row.text, row.style), row.fontId, row.style) * sizePx;
    const item: ProfileBannerTextItem = {
      role: row.role,
      text: caseOf(row.text, row.style),
      x: Math.round(centreText ? box.x + (box.w - width) / 2 : columnX),
      y: Math.round(y),
      maxWidth: Math.floor(centreText ? box.w : columnWidth),
      sizePx,
      fontId: row.fontId,
      style: row.style,
      colour: row.role === 'line' || row.role === 'eyebrow' ? 'accent' : 'text',
      alpha: row.role === 'link' ? 0.9 : 1,
      glow: glow && row.role !== 'link',
    };
    y += sizePx * PROFILE_BANNER_LINE_HEIGHT + colGap * scale;
    return item;
  });
  const cover = withCover
    ? {
        x: Math.round(left),
        y: Math.round(box.y + (box.h - coverSize) / 2),
        size: Math.round(coverSize),
        radius: Math.round(coverSize * 0.04),
      }
    : null;
  return { cover, texts };
}
